import {
  Stack,
  MenuItem,
  Box,
  Paper,
  Typography,
  FormControl,
} from "@mui/material"
import {
  Control,
  FieldErrors,
  UseFormClearErrors,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form"
import { useCallback, useContext, useEffect, useState } from "react"
import React from "react"
import { TSale } from "../../../../../core/types/Sale"
import {
  IProductPricePaymentInstallment,
  TPaymentMethodEnum,
} from "../../../../../core/types/Product"
import { CreditCard } from "../../../../../core/types/CreditCard"
import NumberFunctions from "../../../../../core/functions/NumberFunctions"
import { creditsCards } from "../../../../../core/constants"
import TextFieldRequired from "../../../../Inputs/TextFieldRequired"
import SelectRequired from "../../../../Inputs/SelectRequired"
import { CheckoutContext } from "../../../../../core/contexts/CheckoutContext"
import { maskCardNumber, maskMonthYear } from "../../../../../masks/masks"
import { checkoutPayment7Style } from "./PaymentConsts"
import PriceInput from "../../../../Inputs/PriceInput"
import OrderController from "../../../../../core/controllers/OrderController"
import { IErrorResponse } from "../../../../../core/types/ErrorResponse"
import { ICalculateInstallment } from "../../../../../core/types/Order"
import Promises from "../../../../../core/functions/promises"
import { validateCvv, cvvDigitsOnly, getCvvLength } from "../../../../../core/functions/CvvValidation"
import { useGatewayContext } from "../../../../../core/contexts/GatewayContext"
import StripeCardForm from "../../../../Checkout/StripeCardForm"
import WalletPaymentButtons from "../../../../Checkout/WalletPaymentButtons"
import { useTranslation } from "react-i18next"
import { translateFrequency } from "../../../../../core/i18n/frequency"

interface ICreditCardPaymentPrice7Props {
  visible: boolean
  title?: string
  control: Control<TSale, any>
  errors: FieldErrors<TSale>
  installments: IProductPricePaymentInstallment[] | undefined
  index: number
  isMultiple?: boolean
  watch: UseFormWatch<TSale>
  setValue: UseFormSetValue<TSale>
  clearErrors: UseFormClearErrors<TSale>
}

const CreditCardPaymentPrice7 = (props: ICreditCardPaymentPrice7Props) => {
  const {
    visible,
    title,
    control,
    installments,
    index,
    isMultiple = false,
    watch,
    setValue,
    clearErrors,
  } = props
  const { productPrice: data, installment, displayCurrency } = useContext(CheckoutContext)
  const { useStripe: useStripePayment, stripeFormRef, stripePromise } = useGatewayContext()
  const { t } = useTranslation()

  const buyerName = watch('name')
  const buyerEmail = watch('email')

  const [creditCard, setCreditCard] = useState<CreditCard | undefined>()
  const [errorValue, setErrorValue] = useState("")
  const [creditCardInstallments, setCreditCardInstallments] = useState<
    Array<IProductPricePaymentInstallment>
  >([])

  const saleValueTotal = watch("payment.total")
  const payments = watch("payment.payments")
  const paymentCardValue = watch(`payment.payments.${index}.value`)
  const paymentCardNumber = watch(
    `payment.payments.${index}.creditCardData.number`
  )
  const paymentCardValidate = watch(
    `payment.payments.${index}.creditCardData.validate`
  )
  const paymentCardCVV = watch(`payment.payments.${index}.creditCardData.cvv`)
  const paymentInstallment = watch("payment.installment")
  // Pagamento por wallet (Google Pay/Apple Pay) nao passa pelos campos digitados:
  // enquanto houver walletToken eles nao podem ser obrigatorios, senao o
  // handleSubmit do RHF nunca chega no Submit. Ver finding C1.
  const walletTokenPresent = !!watch(
    `payment.payments.${index}.creditCardData.walletToken`
  )
  const typedCardRequired = visible && !walletTokenPresent

  const firstPurchasePrice = data?.firstPurchasePrice ?? 0
  const isFirstPurchasePrice = firstPurchasePrice > 0
  const frequencyDescription = translateFrequency(t, data?.frequency)
  const complement = isFirstPurchasePrice
    ? t("checkout.card.firstPeriod", { period: frequencyDescription.toLowerCase() })
    : ""
  const installmentCurrent = installment?.original?.find(
    (inst) => inst.installmentNumber === paymentInstallment
  )

  const getInstallments = (
    installmentsData: IProductPricePaymentInstallment[] | undefined
  ) => {
    const list = installmentsData?.sort((a, b) =>
      a.installmentNumber > b.installmentNumber ? -1 : 1
    )
    const items =
      list?.map((x) => {
        const key = x.installmentNumber
        const value = NumberFunctions.formatMoneyCurrency(x.value, displayCurrency)
        const installmentFees = x.isInterestFree ? t("checkout.card.interestFree") : ""
        const label = t("checkout.orderItem.installmentOf", {
          number: key,
          value,
          complement: `${complement}${installmentFees}`,
        })
        return {
          key,
          label,
        }
      }) || []

    return items
  }

  const itensMenu =
    creditCardInstallments.length > 0
      ? getInstallments(creditCardInstallments)
      : getInstallments(installments)

  const getValidate = (value: string) => {
    const cardValidate = value.split("/")
    if (cardValidate.length > 1) {
      const month = Number(cardValidate[0])
      const year = Number(cardValidate[1])
      return { month, year }
    }
    return undefined
  }

  const handleLoadCalculateInstallments = useCallback(
    async (newValue: number) => {
      try {
        await Promises.delay(200)
        const response = await OrderController.calculeIsntallments({
          priceId: data?.id ?? "",
          newValue,
        })
        const responseError = response as IErrorResponse
        const responseData = response as ICalculateInstallment

        if (responseError.code) {
        } else {
          setValue(
            `payment.payments.${index}.installments`,
            responseData.installments
          )
          setCreditCardInstallments(responseData.installments)
        }
      } finally {
      }
    },
    [data, setValue, setCreditCardInstallments]
  )

  const handleChangeCardValidate = (value?: any) => {
    if (!value) return true
    const validate = getValidate(value)
    if (validate) {
      try {
        const now = new Date()
        const nowMonth = now.getMonth() + 1
        const nowYear = Number(now.getFullYear().toString().slice(2))
        if (validate.year < nowYear) return false
        if (validate.month < nowMonth && validate.year === nowYear) return false
      } catch (e) {
        console.error(e)
      }
      return validate.month > 0 && validate.month <= 12
    }
    return value && value !== ""
  }

  const getTestComponentName = (name: string) => {
    return index === 0 ? name : `${name}${(index + 1).toString()}`
  }

  /*const nextInput = () => {
    const inputs = Array.prototype.slice.call(
      document.querySelectorAll("input")
    )
    const currInput = document.activeElement
    const currInputIndex = inputs.indexOf(currInput)
    const nextinputIndex = (currInputIndex + 1) % inputs.length
    const input = inputs[nextinputIndex]
    if (input) {
      input.focus()
    }
  }*/

  useEffect(() => {
    var exists = false
    creditsCards.map((cc) => {
      if (cc.compare(paymentCardNumber)) {
        setCreditCard(cc)
        exists = true
      }
      return true
    })
    if (!exists) {
      setCreditCard(undefined)
    }
  }, [paymentCardNumber])

  useEffect(() => {
    const expectedLength = getCvvLength(paymentCardNumber)
    if ((paymentCardCVV || "").length === expectedLength) {
      document.getElementById("buttonCommit")?.focus()
    }
  }, [paymentCardCVV, paymentCardNumber])

  useEffect(() => {
    const cardNumberComplete = maskCardNumber(paymentCardNumber)
    setValue(
      `payment.payments.${index}.creditCardData.number`,
      cardNumberComplete
    )
    if (cardNumberComplete.length > 18) {
      //nextInput()
      const cardNameInput = document.getElementById(
        `payment.payments.${index}.creditCardData.name`
      )
      if (cardNameInput) {
        cardNameInput.focus()
      }
    }
  }, [paymentCardNumber, setValue])

  useEffect(() => {
    const cardValidateComplete = maskMonthYear(paymentCardValidate)
    setValue(
      `payment.payments.${index}.creditCardData.validate`,
      cardValidateComplete
    )
    if (cardValidateComplete.length > 4) {
      const cardValidate = getValidate(cardValidateComplete)
      if (cardValidate) {
        setValue(
          `payment.payments.${index}.creditCardData.month`,
          cardValidate.month
        )
        setValue(
          `payment.payments.${index}.creditCardData.year`,
          cardValidate.year
        )
      }
      //nextInput()
      const cardNameInput = document.getElementById(
        `payment.payments.${index}.creditCardData.cvv`
      )
      if (cardNameInput) {
        cardNameInput.focus()
      }
    }
  }, [paymentCardValidate, setValue])

  useEffect(() => {
    if (index === 0 && paymentCardValue) {
      if (payments.length > 1) {
        const diff = saleValueTotal - Number(paymentCardValue)
        setValue("payment.payments.1.value", Math.max(0, diff))
      }
    }

    if ((paymentCardValue ?? 5) < 5) {
      setErrorValue(
        t("checkout.card.valueMustBeGreater", {
          value: NumberFunctions.formatMoneyCurrency(5, displayCurrency),
        })
      )
    }

    handleLoadCalculateInstallments(paymentCardValue ?? 0)
  }, [paymentCardValue])

  useEffect(() => {
    if (index === 0 && saleValueTotal) {
      if (payments.length > 1) {
        setValue(`payment.payments.0.value`, 5)
        setValue(`payment.payments.1.value`, saleValueTotal - 5)
      } else if (payments.length === 1) {
        setValue(`payment.payments.0.value`, saleValueTotal)
      }
    }
  }, [saleValueTotal])

  return (
    <Box key={index.toString()} sx={{ display: visible ? "block" : "none" }}>
      <Paper elevation={0} sx={{ backgroundColor: "#fbfbfb" }}>
        <Stack direction="column" spacing={2}>
          {title && isMultiple && (
            <Stack direction="row" spacing={1} alignItems="center">
              {checkoutPayment7Style[TPaymentMethodEnum.CreditCard].icon[0]}
              <Typography
                variant="body1"
                fontSize="14px"
                fontWeight={600}
                lineHeight="18px"
              >
                {title}
              </Typography>
            </Stack>
          )}
          {isMultiple && (
            <FormControl fullWidth>
              <PriceInput
                size="small"
                label={t("checkout.card.amountToPay")}
                value={!paymentCardValue ? 0 : paymentCardValue}
                error={errorValue !== ""}
                InputProps={{
                  inputProps: {
                    min: 5,
                    "data-cy": getTestComponentName("cardValue"),
                    "data-testid": getTestComponentName("cardValue"),
                  },
                  sx: {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: errorValue !== "" ? "red" : "#BCC1FF",
                      borderWidth: "2px",
                    },
                    "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
                      borderColor: errorValue !== "" ? "red" : "#BCC1FF",
                      borderWidth: "2px",
                    },
                  },
                }}
                onChange={(value) => {
                  setValue(`payment.payments.${index}.value`, value)
                  setErrorValue("")
                }}
                disabled={index > 0}
              />
              <Typography
                lineHeight="14px"
                fontSize="12px"
                variant="body2"
                sx={{
                  color: "#5D5E61",
                  pt: 0.5,
                }}
              >
                {t("checkout.card.minValue", {
                  value: NumberFunctions.formatMoneyCurrency(5, displayCurrency),
                })}
              </Typography>
              {errorValue !== "" && (
                <Typography variant="overline" color={"red"}>
                  {errorValue}
                </Typography>
              )}
            </FormControl>
          )}
          {!isMultiple && <WalletPaymentButtons amount={saleValueTotal} />}
          {useStripePayment ? (
            <StripeCardForm
              ref={stripeFormRef}
              stripePromise={stripePromise}
              buyerName={buyerName}
              buyerEmail={buyerEmail}
            />
          ) : (
            <>
              <TextFieldRequired
                control={control}
                variant="rounded"
                fieldName={`payment.payments.${index}.creditCardData.number`}
                label={t("checkout.card.number")}
                placeholder={t("checkout.card.numberPlaceholder")}
                inputProps={{
                  inputMode: "numeric",
                  maxLength: 19,
                  "data-cy": getTestComponentName("cardNumber"),
                  "data-testid": getTestComponentName("cardNumber"),
                }}
                dataCyErrorLabel="cardNumberError"
                clearErrors={clearErrors}
                required={typedCardRequired}
                endAdornment={
                  creditCard && (
                    <img
                      src={creditCard.getImageUrl()}
                      alt="credit card"
                      style={{ height: 30, objectFit: "contain" }}
                    />
                  )
                }
              />
              <TextFieldRequired
                control={control}
                variant="rounded"
                fieldName={`payment.payments.${index}.creditCardData.name`}
                label={t("checkout.card.name")}
                placeholder={t("checkout.card.namePlaceholder")}
                clearErrors={clearErrors}
                required={typedCardRequired}
                inputProps={{
                  "data-cy": getTestComponentName("cardName"),
                  "data-testid": getTestComponentName("cardName"),
                }}
                dataCyErrorLabel="cardNameError"
              />
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{ width: "100%" }}
              >
                <TextFieldRequired
                  control={control}
                  variant="rounded"
                  fieldName={`payment.payments.${index}.creditCardData.validate`}
                  label={t("checkout.card.expiry")}
                  placeholder={t("checkout.card.expiryPlaceholder")}
                  inputProps={{
                    inputMode: "numeric",
                    maxLength: 5,
                    "data-cy": getTestComponentName("cardValidate"),
                    "data-testid": getTestComponentName("cardValidate"),
                  }}
                  dataCyErrorLabel="cardValidateError"
                  clearErrors={clearErrors}
                  required={typedCardRequired}
                  validate={handleChangeCardValidate}
                />
                <TextFieldRequired
                  control={control}
                  variant="rounded"
                  fieldName={`payment.payments.${index}.creditCardData.cvv`}
                  label={t("checkout.card.cvv")}
                  placeholder="999"
                  inputProps={{
                    inputMode: "numeric",
                    maxLength: 4,
                    "data-cy": getTestComponentName("cardCCV"),
                    "data-testid": getTestComponentName("cardCCV"),
                  }}
                  dataCyErrorLabel="cardCCVError"
                  clearErrors={clearErrors}
                  required={typedCardRequired}
                  validate={
                    typedCardRequired
                      ? (value) => validateCvv(value, paymentCardNumber)
                      : undefined
                  }
                  transform={cvvDigitsOnly}
                />
              </Stack>
            </>
          )}
          {!useStripePayment && (
            <Box>
              <SelectRequired
                control={control}
                variant="rounded"
                fieldName={`payment.installment`}
                label={t("checkout.v7.card.installments")}
                required={visible}
                backgroundColorTitle="white"
                disabled={index > 0}
              >
                {itensMenu.map((option) => (
                  <MenuItem key={`${index}-${option.key}`} value={option.key}>
                    {option.label}
                  </MenuItem>
                ))}
              </SelectRequired>
              {isFirstPurchasePrice && (
                <Typography fontSize="14px" lineHeight="16px" sx={{ mt: 1 }}>
                  {t("checkout.card.renewal", {
                    number: paymentInstallment,
                    value: NumberFunctions.formatMoneyCurrency(installmentCurrent?.value, displayCurrency),
                    period: frequencyDescription.toLowerCase(),
                  })}
                </Typography>
              )}
            </Box>
          )}
        </Stack>
      </Paper>
    </Box>
  )
}

export default CreditCardPaymentPrice7
